import { useState } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import Badge from "components/base/Badge";
import FeatherIcon from "feather-icons-react";
import { DealCardProps } from "interfaces/deal";
import { Card, Collapse, Table } from "react-bootstrap";
import { currencyFormat, dateFormatter } from "helpers/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faHandshake, faBolt, faEye, faDollarSign } from "@fortawesome/free-solid-svg-icons";
import {
  UilFolder,
  UilFolderOpen,
  UilUserSquare,
} from "@iconscout/react-unicons";

const DealCard = ({ deal }: DealCardProps) => {
  const [showDetails, setShowDetails] = useState<string | number | null>(null);

  const handleShowDetails = (id: string | number) => {
    if (showDetails === id) {
      setShowDetails(null);
    } else {
      setShowDetails(deal.id);
    }
  };

  return (
    <Card 
      className="mb-3 ai-deal-card"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '12px',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 212, 255, 0.15)';
        e.currentTarget.style.borderColor = '#00d4ff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
        e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.2)';
      }}
    >
      <Card.Body style={{ padding: '16px' }}>
        {/* Enhanced Header with AI Glow */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={faBolt} 
              style={{ 
                color: '#00d4ff',
                fontSize: '12px',
                marginRight: '8px',
                filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.3))'
              }}
            />
            <p className="mb-0 fs-9 fw-semibold text-muted">
              {dateFormatter(deal?.created_on)}
            </p>
          </div>
          
          <button
            className="btn p-0 deal-collapse-btn"
            onClick={() => handleShowDetails(deal?.id)}
            style={{
              background: 'rgba(0, 212, 255, 0.1)',
              borderRadius: '6px',
              padding: '4px 6px',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 212, 255, 0.2)';
              e.currentTarget.style.borderColor = '#00d4ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.2)';
            }}
          >
            <FontAwesomeIcon
              icon={faAngleDown}
              className={classNames("fs-8", {
                show: showDetails === deal?.id,
              })}
              style={{
                color: '#00d4ff',
                transform: showDetails === deal?.id ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease'
              }}
            />
          </button>
        </div>
        
        {/* Enhanced Deal Name and Price */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Link
            to={`/deals/${deal?.id}?type=activity`}
            className="fw-bold line-clamp-1 me-3 fs-7"
            style={{
              color: '#1f2937',
              textDecoration: 'none',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#00d4ff';
              e.currentTarget.style.textShadow = '0 0 8px rgba(0, 212, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#1f2937';
              e.currentTarget.style.textShadow = 'none';
            }}
          >
            {deal?.deal_name}
          </Link>
          
          <div 
            className="price-badge d-flex align-items-center"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              borderRadius: '8px',
              padding: '4px 8px',
              border: '1px solid rgba(16, 185, 129, 0.2)'
            }}
          >
            <FontAwesomeIcon 
              icon={faDollarSign} 
              className="text-success me-1" 
              style={{ fontSize: '10px' }}
            />
            <span className="fs-9 fw-semibold text-success">
              {currencyFormat(deal.deal_price, { minimumFractionDigits: 0 })}
            </span>
          </div>
        </div>
        
        {/* Enhanced MLS ID Display */}
        {deal.deal_mls_id && (
          <div
            className={classNames(
              "d-flex justify-content-between align-items-center mb-2",
              {
                "d-none": showDetails === deal.id,
              }
            )}
            style={{
              background: 'rgba(0, 212, 255, 0.05)',
              borderRadius: '8px',
              padding: '8px 12px',
              border: '1px solid rgba(0, 212, 255, 0.1)'
            }}
          >
            <span className="fw-semibold" style={{ color: '#6b7280', fontSize: '12px' }}>
              MLS ID:
            </span>
            <span className="fw-bold" style={{ color: '#1f2937', fontSize: '12px' }}>
              {deal.deal_mls_id}
            </span>
          </div>
        )}

        {/* Enhanced Collapse Details */}
        <Collapse in={showDetails === deal.id}>
          <div 
            className="mt-3"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(248, 249, 250, 0.5) 100%)',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(0, 212, 255, 0.1)'
            }}
          >
            {/* Enhanced Status Badges */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <Badge 
                variant="phoenix" 
                bg="info"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '10px',
                  padding: '4px 8px',
                  fontWeight: '600'
                }}
              >
                {deal.deal_lead_status}
              </Badge>
              <Badge 
                variant="phoenix" 
                bg="danger"
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '10px',
                  padding: '4px 8px',
                  fontWeight: '600'
                }}
              >
                {deal.deal_lead_type}
              </Badge>
            </div>
            
            {/* Enhanced Details Table */}
            <div 
              className="scrollbar mb-3"
              style={{
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '10px',
                padding: '12px'
              }}
            >
              <Table className="mb-0 w-100 align-middle" borderless size="sm">
                <tbody>
                  <tr>
                    <td className="d-flex gap-2 align-items-center" style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.1)', paddingBottom: '8px' }}>
                      <UilFolderOpen size={14} style={{ color: '#00d4ff' }} />
                      <p className="fw-semibold fs-9 mb-0" style={{ color: '#6b7280' }}>
                        Opening Date
                      </p>
                    </td>
                    <td style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.1)', paddingBottom: '8px' }}>:</td>
                    <td className="fw-semibold fs-9 mb-0" style={{ color: '#1f2937', borderBottom: '1px solid rgba(0, 212, 255, 0.1)', paddingBottom: '8px' }}>
                      {dateFormatter(deal.created_on)}
                    </td>
                  </tr>
                  <tr>
                    <td className="d-flex gap-2 align-items-center" style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.1)', paddingBottom: '8px', paddingTop: '8px' }}>
                      <UilFolder size={14} style={{ color: '#00d4ff' }} />
                      <p className="fw-semibold fs-9 mb-0" style={{ color: '#6b7280' }}>
                        Closing Date
                      </p>
                    </td>
                    <td style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.1)', paddingBottom: '8px', paddingTop: '8px' }}>:</td>
                    <td className="fw-semibold fs-9 mb-0" style={{ color: '#1f2937', borderBottom: '1px solid rgba(0, 212, 255, 0.1)', paddingBottom: '8px', paddingTop: '8px' }}>
                      {deal.deal_closing_date
                        ? dateFormatter(deal.deal_closing_date)
                        : "N/A"}
                    </td>
                  </tr>
                  <tr>
                    <td className="d-flex gap-2 align-items-center" style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.1)', paddingBottom: '8px', paddingTop: '8px' }}>
                      <UilUserSquare size={14} style={{ color: '#00d4ff' }} />
                      <p className="fw-semibold fs-9 mb-0" style={{ color: '#6b7280' }}>
                        Customers
                      </p>
                    </td>
                    <td style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.1)', paddingBottom: '8px', paddingTop: '8px' }}>:</td>
                    <td className="fw-semibold fs-9 mb-0" style={{ color: '#1f2937', borderBottom: '1px solid rgba(0, 212, 255, 0.1)', paddingBottom: '8px', paddingTop: '8px' }}>
                      {deal?.customers?.length || "0"}
                    </td>
                  </tr>
                  <tr>
                    <td className="d-flex gap-2 align-items-center" style={{ paddingTop: '8px' }}>
                      <FontAwesomeIcon
                        icon={faHandshake}
                        style={{ color: '#00d4ff', fontSize: '14px' }}
                      />
                      <p className="fw-semibold fs-9 mb-0" style={{ color: '#6b7280' }}>
                        Related Deals
                      </p>
                    </td>
                    <td style={{ paddingTop: '8px' }}>:</td>
                    <td className="fw-semibold fs-9 mb-0" style={{ color: '#1f2937', paddingTop: '8px' }}>
                      {deal?.related_deals?.length > 0
                        ? deal?.related_deals?.length
                        : "0"}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
            
            {/* Enhanced Inspection Period */}
            {deal?.deal_inspection_period && (
              <div 
                className="inspection-period"
                style={{
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                  borderRadius: '8px',
                  padding: '12px',
                  border: '1px solid rgba(245, 158, 11, 0.2)'
                }}
              >
                <p className="fs-9 mb-1 fw-semibold" style={{ color: '#92400e' }}>
                  Inspection Period
                </p>
                <p className="align-items-center d-flex fs-9 fw-bold gap-2 mb-0" style={{ color: '#1f2937' }}>
                  <FontAwesomeIcon 
                    icon={faEye} 
                    style={{ color: '#f59e0b', fontSize: '12px' }}
                  />
                  {deal?.deal_inspection_period} Days
                </p>
              </div>
            )}
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
};

export default DealCard;
